import { put, all, takeEvery, takeLeading } from 'redux-saga/effects';
import {
  getAssessorEmployeeMappingFailureAction,
  getAssessorEmployeeMappingSuccessAction,
  getUserByAssessorFailureAction,
  getUserByAssessorSuccessAction,
  getUserFailureAction,
  getUserSuccessAction,
  updateAssessorFailureAction,
  updateAssessorSuccessAction,
  getUserManagementSuccessAction,
  getUserManagementFailureAction,
  getUserDetailsSuccessAction,
  getUserDetailsFailureAction,
  updateUserPreferenceSuccessAction,
  updateUserPreferenceFailureAction,
  submitSkillChangeRequestSuccessAction,
  submitSkillChangeRequestFailureAction,
  getUserProfile as getUserProfileAction,
  notifyUserSuccessAction,
  notifyUserFailureAction,
  checkUserExistSuccessAction,
  checkUserExistFailureAction,
  saveUserSuccessAction,
  saveUserFailureAction,
  getUserProfileFailureAction,
  getUserProfileSuccessAction
} from '../../features/users/userSlice';
import { apiPython as api } from '../../../service/api';
import { getSessionStorage, isSessionValueReady } from '../../../utils/helpers';
import {
  GET_ASSESSOR_EMPLOYEE_MAPPING,
  GET_USERS_BY_ASSESSOR,
  GET_USERS_BY_ID,
  GET_USER_MANAGEMENT,
  UPDATE_ASSESSOR,
  GET_USER_DETAILS,
  GET_USER_PROFILE,
  UPDATE_USER_PREFERENCE,
  SUBMIT_SKILL_CHANGE_REQUEST,
  SAVE_USER,
  NOTIFY_USER,
  CHECK_USER_EXIST
} from '../../actions/User/actionTypes';
import URLs from '../../../constants/urls';
import {
  setOpenTechnicalModal,
  setTechnicalAssessmentErrorMessage,
  setTechnicalAssessmentsLoader,
  setTechnicalAssessmentSuccessMessage
} from '../../features/technicalAssessments/technicalAssessmentsSlice';
import { getManageExternalUserAction } from '../../features/ManageExternalUsers/ManageExternalUsersSlice';
import { unwrapApiPayload } from '../../../utils/apiPayload';

function* getUserSaga({ payload: { id, loggedUserId } }) {
  try {
    const response = yield api.get(`${URLs.GET_USER}?strQID=${id}&loggedUserId=${loggedUserId}`);
    yield put(getUserSuccessAction(response.data.data));
  } catch (error) {
    yield put(getUserFailureAction({
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data
    }));
  }
}


function* getUserManagement() {
  try {
    const response = yield api.get(`${URLs.GET_USER_MANAGEMENT}`);
    yield put(getUserManagementSuccessAction(unwrapApiPayload(response.data)));
  } catch (error) {
    yield put(getUserManagementFailureAction(error));
  }
}

function* getUserByAssessorSaga({ payload: id }) {
  try {
    const response = yield api.get(`${URLs.GET_USER_BY_ASSESSOR}?strAssessorID=${id}`);
    yield put(getUserByAssessorSuccessAction(response.data.data));
  } catch (error) {
    yield put(getUserByAssessorFailureAction(error));
  }
}

function* getAssessorEmployeeMappingSaga() {
  try {
    const samId = encodeURIComponent(getSessionStorage('USER_ID') || '');
    const permissionLevel = encodeURIComponent(getSessionStorage('PERMISSION_LEVEL') || 'A');
    const response = yield api.get(
      `${URLs.GET_ASSESSOR_EMPLOYEE_MAPPING}?SamId=${samId}&permissionLevel=${permissionLevel}`
    );
    yield put(getAssessorEmployeeMappingSuccessAction(response.data.data));
  } catch (error) {
    yield put(getAssessorEmployeeMappingFailureAction(error));
  }
}

function* updateAssessorSaga({ payload }) {
  try {
    const URL = payload.isUpdate ? URLs.UPDATE_ASSESSOR : URLs.REMOVE_ASSESSOR;
    const raw = payload.body;
    let body;
    try {
      body = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
      yield put(updateAssessorFailureAction('Invalid assessor request body'));
      return;
    }
    const response = yield api.post(URL, body);
    yield put(updateAssessorSuccessAction({ isUpdate: payload.isUpdate, data: response.data.data, user: payload.user }));
    const assessorId =
      body?.assessorid ?? body?.Assessorid ?? body?.assessorId ?? body?.AssessorId ?? body?.strAssessorID;
    try {
      const samId = encodeURIComponent(getSessionStorage('USER_ID') || '');
      const permissionLevel = encodeURIComponent(getSessionStorage('PERMISSION_LEVEL') || 'A');
      const mapResponse = yield api.get(
        `${URLs.GET_ASSESSOR_EMPLOYEE_MAPPING}?SamId=${samId}&permissionLevel=${permissionLevel}`
      );
      yield put(getAssessorEmployeeMappingSuccessAction(mapResponse.data.data));
    } catch (e) {
      yield put(getAssessorEmployeeMappingFailureAction(e));
    }
    if (assessorId) {
      try {
        const usersResponse = yield api.get(`${URLs.GET_USER_BY_ASSESSOR}?strAssessorID=${assessorId}`);
        yield put(getUserByAssessorSuccessAction(usersResponse.data.data));
      } catch (e) {
        yield put(getUserByAssessorFailureAction(e));
      }
    }
  } catch (error) {
    yield put(updateAssessorFailureAction(error));
  }
}

function* getUserDetails({ payload }) {
  try {
    const missingSession =
      !isSessionValueReady(payload?.SDAEmailID) ||
      !isSessionValueReady(payload?.loggedUserID) ||
      !isSessionValueReady(payload?.userType) ||
      !isSessionValueReady(payload?.permissionLevel);

    if (missingSession) {
      yield put(getUserDetailsFailureAction('User session not initialized'));
      return;
    }

    const response = yield api.get(
      `${URLs.GET_USER_DETAILS}?SDAEmailID=${payload.SDAEmailID}&loggedUserID=${payload.loggedUserID}&userType=${payload.userType}&permissionLevel=${payload.permissionLevel}`
    );
    yield put(getUserDetailsSuccessAction(response.data.data));
  } catch (error) {
    yield put(getUserDetailsFailureAction(error));
  }
}

function* getUserProfile({ payload }) {
  try {
    const response = yield api.get(
      `${URLs.GET_USER_PROFILE}?loggedUserID=${payload.loggedUserID}`
    );
    yield put(getUserProfileSuccessAction(response.data.data));
  } catch (error) {
    yield put(getUserProfileFailureAction(error));
  }
}

function* updateUserPreference({ payload }) {
  try {
    const response = yield api.post(`${URLs.UPDATE_USER_PREFERENCES}`, payload);
    const statusCode = response?.data?.statusCode ?? response?.data?.status_code;
    if (statusCode === 200) {
      yield put(updateUserPreferenceSuccessAction('Saved Successfully'));
    } else {
      const msg = response.data?.message || 'Something went wrong';
      yield put(updateUserPreferenceFailureAction(msg));
    }
  } catch (error) {
    const msg = error?.response?.data?.message || 'Something went wrong';
    yield put(updateUserPreferenceFailureAction(msg));
  }
}

function* submitSkillChangeRequest({ payload }) {
  try {
    const response = yield api.post(`${URLs.SUBMIT_SKILL_CHANGE_REQUEST}`, payload);
    if (response.data.statusCode === 200) {
      const message =
        response.data?.data?.message ||
        'Skill update is being reviewed by your manager';
      yield put(submitSkillChangeRequestSuccessAction(message));
      yield put(getUserProfileAction({ loggedUserID: payload.loggedUserID }));
    } else {
      const msg =
        response.data?.data?.error ||
        response.data?.message ||
        'Something went wrong';
      yield put(submitSkillChangeRequestFailureAction(msg));
    }
  } catch (error) {
    const msg =
      error?.response?.data?.data?.error ||
      error?.response?.data?.message ||
      'Something went wrong';
    yield put(submitSkillChangeRequestFailureAction(msg));
  }
}

function* saveUserData({ payload }) {
  try {
    const response = yield api.post(`${URLs.SAVE_USER}`, payload);
    if (response.data) {
      yield put(saveUserSuccessAction('Saved Successfully'));
      yield put(getManageExternalUserAction());
    }
  } catch (error) {
    const msg =
      error?.response?.data?.errors?.LastName?.[0] ||
      error?.response?.data?.message ||
      'Save failed';
    yield put(saveUserFailureAction(msg));
  }
}

function* notifyUser({ payload }) {
  try {
    const response = yield api.get(`${URLs.NOTIFY_USER}?loggedUserID=${payload}`);
    if (response.data.statusCode === 200) {
      yield put(notifyUserSuccessAction('Saved Successfully'));
    }
  } catch (error) {
    yield put(notifyUserFailureAction('Something went wrong'));
  }
}

function* checkUserExist({ payload }) {
  try {
    const response = yield api.get(`${URLs.CHECK_USER_EXISTS}?EmailIds=${payload}`);
    yield put(checkUserExistSuccessAction(response.data));
  } catch (error) {
    yield put(checkUserExistFailureAction('Something went wrong'));
  }
}

export function* watchUser() {
  yield takeEvery(GET_USERS_BY_ID, getUserSaga);
  yield takeEvery(GET_ASSESSOR_EMPLOYEE_MAPPING, getAssessorEmployeeMappingSaga);
  yield takeEvery(GET_USERS_BY_ASSESSOR, getUserByAssessorSaga);
  yield takeEvery(UPDATE_ASSESSOR, updateAssessorSaga);
  yield takeEvery(GET_USER_MANAGEMENT, getUserManagement);
  yield takeEvery(GET_USER_DETAILS, getUserDetails);
  yield takeEvery(GET_USER_PROFILE, getUserProfile);
  yield takeEvery(UPDATE_USER_PREFERENCE, updateUserPreference);
  yield takeEvery(SUBMIT_SKILL_CHANGE_REQUEST, submitSkillChangeRequest);
  yield takeLeading(SAVE_USER, saveUserData);
  yield takeEvery(NOTIFY_USER, notifyUser);
  yield takeEvery(CHECK_USER_EXIST, checkUserExist);
}

export default function* userSaga() {
  yield all([watchUser()]);
}
